import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

const stopEvent = (e) => {
  e.stopPropagation();
};

/** Nota STAGE editable (autosave) para una pieza de reunión. */
export default function MeetingMediaTeamNoteEditor({
  photo,
  onSaveTeamNote,
  t,
  sx,
}) {
  const theme = useTheme();
  const [draft, setDraft] = useState(() => String(photo?.teamNote || ""));
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const timerRef = useRef(null);
  const focusedRef = useRef(false);
  const savedNote = String(photo?.teamNote || "").trim();

  useEffect(() => {
    if (focusedRef.current) return;
    setDraft(String(photo?.teamNote || ""));
  }, [photo?.id, photo?.teamNote]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const dirty = draft !== String(photo?.teamNote || "");

  const save = async (value) => {
    if (typeof onSaveTeamNote !== "function") return;
    setSaving(true);
    try {
      await onSaveTeamNote(photo.id, value);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Box
        sx={{ mt: 0.75, ...sx }}
        onMouseDown={stopEvent}
        onTouchStart={stopEvent}
      >
        <Button
          size="small"
          fullWidth
          variant="text"
          startIcon={
            savedNote ? (
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            ) : (
              <NotesOutlinedIcon sx={{ fontSize: 16 }} />
            )
          }
          disabled={typeof onSaveTeamNote !== "function"}
          onClick={() => setEditing(true)}
          sx={{
            justifyContent: "flex-start",
            textTransform: "none",
            fontWeight: savedNote ? 600 : 700,
            color: savedNote ? "text.primary" : "primary.main",
            bgcolor: savedNote
              ? alpha(theme.palette.success.main, 0.06)
              : alpha(theme.palette.primary.main, 0.04),
            borderRadius: 2,
            px: 1,
            py: 0.65,
            border: "1px solid",
            borderColor: savedNote
              ? alpha(theme.palette.success.main, 0.22)
              : alpha(theme.palette.primary.main, 0.18),
            "&:hover": {
              bgcolor: savedNote
                ? alpha(theme.palette.success.main, 0.1)
                : alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <Typography
            component="span"
            variant="body2"
            sx={{
              textAlign: "left",
              overflow: "hidden",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              minWidth: 0,
              flex: 1,
              lineHeight: 1.35,
              // Hasta ~8 líneas visibles; el resto al editar / scroll del feed
              display: "-webkit-box",
              WebkitLineClamp: 8,
              WebkitBoxOrient: "vertical",
            }}
          >
            {savedNote || t("Meetings.teamNoteAdd")}
          </Typography>
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{ mt: 0.75, ...sx }}
      onMouseDown={stopEvent}
      onTouchStart={stopEvent}
    >
      <TextField
        autoFocus
        value={draft}
        onChange={(e) => {
          const next = e.target.value;
          setDraft(next);
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            if (next !== String(photo?.teamNote || "")) {
              save(next);
            }
          }, 900);
        }}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onBlur={() => {
          focusedRef.current = false;
          if (timerRef.current) clearTimeout(timerRef.current);
          if (dirty) save(draft);
          setEditing(false);
        }}
        multiline
        minRows={3}
        maxRows={16}
        fullWidth
        inputProps={{ maxLength: 8000 }}
        size="small"
        label={t("Meetings.teamNote")}
        placeholder={t("Meetings.teamNoteHint")}
        disabled={typeof onSaveTeamNote !== "function"}
        sx={{
          "& .MuiOutlinedInput-root": {
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            borderRadius: 2,
          },
        }}
      />
      {dirty || saving ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
          <Button
            size="small"
            startIcon={<SaveOutlinedIcon />}
            disabled={saving || !dirty}
            onMouseDown={stopEvent}
            onClick={() => {
              if (timerRef.current) clearTimeout(timerRef.current);
              save(draft);
            }}
          >
            {saving ? t("Meetings.teamNoteSaving") : t("Meetings.teamNoteSave")}
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
}
